import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
import { Previous } from 'grommet-icons';
import { useNavigate } from 'react-router-dom';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
  ConnectPayouts,
} from '@stripe/react-connect-js';
import { useAppTheme } from '@Components/Context';
import {
  Seo, AppButton, AppAnchor, Spinning, ErrorNotice,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import {
  fetchPayoutAccountSession,
  fetchPayoutStatus,
  clearPayoutStatus,
} from '@Actions';
import { paths } from '@Components/configs';

const buildStripeAppearance = (darkMode) => ({
  variables: darkMode ? {
    colorPrimary: '#F94B4B',
    colorBackground: '#14191F',
    colorText: '#FFFFFF',
    colorSecondaryText: '#FE6464',
    colorDanger: '#F94B4B',
    colorBorder: '#394658',
    fontFamily: 'inherit',
    borderRadius: '6px',
    spacingUnit: '10px',
  } : {
    colorPrimary: '#004FFE',
    colorBackground: '#FFFFFF',
    colorText: '#3C3C3C',
    colorSecondaryText: '#585858',
    colorDanger: '#E53E3E',
    colorBorder: '#D8D8D8',
    fontFamily: 'inherit',
    borderRadius: '6px',
    spacingUnit: '10px',
  },
});

const PayoutSetupPage = ({
  small,
  location,
  paymentPending,
  payoutStatus,
  payoutStatusLoading,
  getPayoutAccountSession,
  fetchStatus,
  clearStatus,
}) => {
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const [error, setError] = React.useState(null);
  const [stripeConnect, setStripeConnect] = React.useState(null);

  const initStripeConnect = React.useCallback(async (darkMode) => {
    setError(null);
    const result = await getPayoutAccountSession();
    if (result.success && result.clientSecret) {
      const instance = loadConnectAndInitialize({
        publishableKey: process.env.STRIPE_CONNECT_PUBLISHABLE_KEY,
        fetchClientSecret: async () => result.clientSecret,
        appearance: buildStripeAppearance(darkMode),
      });
      setStripeConnect(instance);
    } else {
      setError(result.error || 'Something went wrong. Please try again.');
    }
  }, [getPayoutAccountSession]);

  // Keep Stripe form in sync when user toggles theme
  React.useEffect(() => {
    if (!stripeConnect) return;
    stripeConnect.update({ appearance: buildStripeAppearance(isDarkMode) });
  }, [isDarkMode, stripeConnect]);

  React.useEffect(() => {
    if (!paymentPending) {
      fetchStatus();
      initStripeConnect(isDarkMode);
    } else {
      navigate(paths.account);
    }
    return () => clearStatus();
  }, [paymentPending]); // eslint-disable-line

  const handleRetry = () => {
    setStripeConnect(null);
    initStripeConnect(isDarkMode);
  };

  const setupComplete = payoutStatus?.setupComplete === true;

  const renderContent = () => {
    if (error) {
      return (
        <Box flex direction="column" align="center" justify="center" pad="2rem">
          <Box direction="column" align="center" gap="1.5rem" width={small ? '80%' : '40%'}>
            <ErrorNotice
              message="Unable to load payout settings"
              messageColor="var(--app-error)"
              iconColor="var(--app-error)"
              backgroundColor="#FCF8EF"
            >
              <Box direction="column" gap="0.75rem">
                <Text color="#3C3C3C" size="0.9rem" style={{ lineHeight: 1.4 }}>
                  {error?.includes('Business Section') ? (
                    <>
                      Your payout setup is incomplete. Please go to your&nbsp;
                      <AppAnchor path={paths.account}>
                        <span style={{ color: page.highlight, textDecoration: 'underline' }}>account page</span>
                      </AppAnchor>
                      &nbsp;and fill in the Business Profile Section.
                    </>
                  ) : error}
                </Text>
                <Text color="#3C3C3C" size="0.9rem" style={{ lineHeight: 1.4 }}>
                  If the issue persists, please contact support at&nbsp;
                  <a href="mailto:contact@redflagai.co" style={{ color: page.highlight, textDecoration: 'none' }}>contact@redflagai.co</a>
                </Text>
              </Box>
            </ErrorNotice>
            <AppButton
              overrideHover
              level="authSubmit"
              bgColor={button.primaryBg}
              color={button.primaryText}
              label="Try again"
              alignSelf="center"
              onClick={handleRetry}
            />
          </Box>
        </Box>
      );
    }

    if (!stripeConnect || payoutStatusLoading) {
      return (
        <Box flex direction="column" align="center" justify="center" gap="1.25rem" pad="2rem">
          <Spinning size="large" color={page.highlight} />
          <Text size="1rem" color={page.primaryText}>
            Loading payout settings…
          </Text>
        </Box>
      );
    }

    return (
      <Box flex direction="column" pad={{ vertical: '1.5rem', horizontal: small ? '1rem' : '1.5rem' }}>
        <ConnectComponentsProvider connectInstance={stripeConnect}>
          {setupComplete ? (
            <ConnectPayouts />
          ) : (
            <ConnectAccountOnboarding
              onExit={() => navigate(paths.payoutStatus)}
            />
          )}
        </ConnectComponentsProvider>
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: setupComplete ? 'Payout settings' : 'Payout setup',
        subTitle: 'Plan and profile settings',
        bannerChildrenPosition: 'bottom',
        bannerChildren: (
          <Box width="fit-content">
            <AppButton
              overrideHover
              onClick={() => navigate(paths.account)}
              label="Go back"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Previous size="0.8rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      <Box flex background={page.background}>
        {renderContent()}
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    paymentPending: state.auth.paymentPending,
    payoutStatus: state.payment.payoutStatus,
    payoutStatusLoading: state.payment.payoutStatusLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    getPayoutAccountSession: fetchPayoutAccountSession,
    fetchStatus: fetchPayoutStatus,
    clearStatus: clearPayoutStatus,
  }, dispatch);
}

PayoutSetupPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  paymentPending: PropTypes.bool.isRequired,
  payoutStatus: PropTypes.shape({
    setupComplete: PropTypes.bool,
  }),
  payoutStatusLoading: PropTypes.bool.isRequired,
  getPayoutAccountSession: PropTypes.func.isRequired,
  fetchStatus: PropTypes.func.isRequired,
  clearStatus: PropTypes.func.isRequired,
};

PayoutSetupPage.defaultProps = {
  payoutStatus: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(PayoutSetupPage));
